import { defineComponent as _defineComponent } from 'vue'
import { createTextVNode as _createTextVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, createVNode as _createVNode, createElementVNode as _createElementVNode, toDisplayString as _toDisplayString, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, withModifiers as _withModifiers, normalizeClass as _normalizeClass, renderList as _renderList, Fragment as _Fragment, createBlock as _createBlock } from "vue"

const _hoisted_1 = { class: "report-info" }
const _hoisted_2 = { class: "drop-zone__content" }
const _hoisted_3 = {
  key: 0,
  class: "drop-zone__text"
}
const _hoisted_4 = {
  key: 1,
  class: "drop-zone__file"
}
const _hoisted_5 = { class: "drop-zone__filename" }
const _hoisted_6 = { class: "paste-zone" }
const _hoisted_7 = { class: "examples-grid" }

import { computed, ref } from 'vue'
import { useTabsStore } from '@/stores/tabs'

interface ExampleDef {
  type: string
  label: string
  icon: string
}

interface ReportFileData {
  version: string
  exportedAt?: string
  name: string
  state: string
  report: unknown[]
}

type FileResult = 'ok' | 'report' | 'error'
type DataType = 'hex' | 'json'


export default /*@__PURE__*/_defineComponent({
  __name: 'UploadCard',
  emits: ["upload", "reportLoaded"],
  setup(__props, { emit: __emit }) {

const store = useTabsStore()

const emit = __emit

// -----------------------------------------------------------------------------
// State

const activeTab = ref('file')

const data = ref<string | null>(null)
const file = ref<File | null>(null)

const pastedContent = ref('')
const dragOver = ref(false)

const dialog = ref(false)
const reportDialog = ref(false)

const reportData = ref<ReportFileData | null>(null)

const reportInfo = ref({
  name: '',
  exportedAt: '',
  resultCount: 0
})

const fileInputRef = ref<HTMLInputElement | null>(null)

// Error snackbar
const errorSnackbar = ref(false)
const errorMessage = ref('')

// -----------------------------------------------------------------------------
// Derived state

const fileName = computed(() => file.value?.name ?? '')

const dataType = computed<DataType>(() => {
  return file.value?.name.toLowerCase().endsWith('.json')
    ? 'json'
    : 'hex'
})

// -----------------------------------------------------------------------------
// Examples

const examples: ExampleDef[] = [
  {
    type: 'roa',
    label: 'ROA',
    icon: 'mdi-shield-check-outline'
  },
  {
    type: 'mft',
    label: 'Manifest',
    icon: 'mdi-format-list-checks'
  },
  {
    type: 'crl',
    label: 'CRL',
    icon: 'mdi-close-circle-outline'
  },
  {
    type: 'cer',
    label: 'Certificate',
    icon: 'mdi-certificate-outline'
  },
  {
    type: 'asa',
    label: 'ASPA',
    icon: 'mdi-link-variant'
  },
  {
    type: 'gbr',
    label: 'Ghostbuster',
    icon: 'mdi-ghost-outline'
  },
  {
    type: 'tls',
    label: 'TLS',
    icon: 'mdi-lock-outline'
  },
  {
    type: 'csr',
    label: 'CSR',
    icon: 'mdi-file-key-outline'
  }
]

function loadExample(type: string): void {
  store.addTab(`${type}_example`)

  store.stateSet({
    tab: store.currentTab,
    type: 'example',
    data: type
  })

  emit('upload')
}

function open(newTab: boolean): void {
  if (!data.value) {
    dialog.value = false
    return
  }

  dialog.value = false

  if (newTab) {
    store.addTab(fileName.value || 'Unnamed')
  }

  try {
    store.stateSet({
      tab: store.currentTab,
      data: data.value,
      type: dataType.value
    })

    clearFile()
    emit('upload')
  } catch (error) {
    console.error('Failed to load content:', error)
    showError(getUserFriendlyError(error))
  }
}

function parseReport(text: string): ReportFileData | null {
  try {
    const parsed: unknown = JSON.parse(text)

    if (
      !parsed ||
      typeof parsed !== 'object'
    ) {
      return null
    }

    const report = parsed as Partial<ReportFileData>

    if (
      !report.version ||
      !report.state ||
      !Array.isArray(report.report)
    ) {
      return null
    }

    return {
      version: report.version,
      exportedAt: report.exportedAt,
      name: report.name ?? 'Unknown',
      state: report.state,
      report: report.report
    }
  } catch {
    return null
  }
}

function loadReportObject(newTab: boolean): void {
  const report = reportData.value

  if (!report) {
    reportDialog.value = false
    return
  }

  reportDialog.value = false

  if (newTab) {
    store.addTab(
      report.name || 'Loaded from Report'
    )
  }

  store.stateSet({
    tab: store.currentTab,
    data: report.state,
    type: 'json'
  })

  emit('reportLoaded', {
    name: report.name,
    state: report.state,
    report: report.report
  })

  clearFile()
  emit('upload')
}

// -----------------------------------------------------------------------------
// File processing

async function processFile(
  currentFile: File
): Promise<FileResult> {
  try {
    const isJson = currentFile.name
      .toLowerCase()
      .endsWith('.json')

    if (isJson) {
      const text = await currentFile.text()
      const report = parseReport(text)

      if (report) {
        reportData.value = report

        reportInfo.value = {
          name: report.name || 'Unknown',
          exportedAt: report.exportedAt
            ? new Date(report.exportedAt).toLocaleString()
            : 'Unknown',
          resultCount: report.report.length
        }

        reportDialog.value = true

        return 'report'
      }

      data.value = text
    } else {
      const buffer = await currentFile.arrayBuffer()
      data.value = decodeBuffer(buffer)
    }

    file.value = currentFile

    return 'ok'
  } catch (error) {
    console.error(
      `Error processing file "${currentFile.name}":`,
      error
    )

    showError(
      `Error processing ${currentFile.name}`
    )

    return 'error'
  }
}

function decodeBuffer(buffer: ArrayBuffer): string {
  try {
    return new TextDecoder('utf-8', {
      fatal: true
    }).decode(buffer)
  } catch {
    return bytesToHex(new Uint8Array(buffer))
  }
}

function bytesToHex(bytes: Uint8Array): string {
  let result = ''

  for (const byte of bytes) {
    result += byte
      .toString(16)
      .padStart(2, '0')
      .toUpperCase()
  }

  return result
}

// -----------------------------------------------------------------------------
// File input

function triggerFileInput(): void {
  fileInputRef.value?.click()
}

function handleFileSelect(event: Event): void {
  const input = event.target as HTMLInputElement

  void handleFiles(input.files)

  // Allow selecting the same file again.
  input.value = ''
}

function handleDrop(event: DragEvent): void {
  dragOver.value = false

  void handleFiles(
    event.dataTransfer?.files
  )
}

async function handleFiles(
  list: FileList | null | undefined
): Promise<void> {
  if (!list?.length) {
    return
  }

  const files = Array.from(list)
  const multiple = files.length > 1

  for (const currentFile of files) {
    const result = await processFile(currentFile)

    if (result === 'error') {
      continue
    }

    // Report files require user interaction.
    if (result === 'report') {
      break
    }

    if (
      multiple ||
      store.tabs.length === 0
    ) {
      open(true)
    } else {
      dialog.value = true
      break
    }
  }
}

function handlePastedContent(): void {
  const content = pastedContent.value.trim()

  if (!content) {
    showError('Please paste some content first.')
    return
  }

  data.value = normalizePastedContent(content)
  file.value = null

  if (store.tabs.length === 0) {
    open(true)
  } else {
    dialog.value = true
  }

  pastedContent.value = ''
}

function normalizePastedContent(
  content: string
): string {
  const trimmed = content.trim()

  // Remove whitespace from hexadecimal content.
  if (/^[0-9a-f\s]+$/i.test(trimmed)) {
    return trimmed.replace(/\s+/g, '')
  }

  return trimmed
}

function clearFile(): void {
  file.value = null
  data.value = null
  reportData.value = null
  reportDialog.value = false

  if (fileInputRef.value) {
    fileInputRef.value.value = ''
  }
}

// -----------------------------------------------------------------------------
// Error handling

function showError(message: string): void {
  errorMessage.value = message
  errorSnackbar.value = true
}

function getUserFriendlyError(error: unknown): string {
  const message =
    error instanceof Error
      ? error.message
      : String(error)

  if (message.includes('neither hex nor base64')) {
    return 'Invalid content. Please paste a valid hexadecimal or Base64 value.'
  }

  if (message.includes('Invalid hex data')) {
    return 'Invalid hexadecimal data.'
  }

  if (message.includes('Invalid base64 data')) {
    return 'Invalid Base64 data.'
  }

  if (message.includes('Invalid data3')) {
    return 'The content was decoded, but it is not a valid RPKI object.'
  }

  if (message.includes('looked like PEM')) {
    return 'The PEM data is incomplete or invalid.'
  }

  return 'Unable to process the content. Please check the input and try again.'
}

return (_ctx: any,_cache: any) => {
  const _component_v_card_title = _resolveComponent("v-card-title")!
  const _component_v_card_text = _resolveComponent("v-card-text")!
  const _component_v_btn = _resolveComponent("v-btn")!
  const _component_v_card_actions = _resolveComponent("v-card-actions")!
  const _component_v_card = _resolveComponent("v-card")!
  const _component_v_dialog = _resolveComponent("v-dialog")!
  const _component_v_icon = _resolveComponent("v-icon")!
  const _component_v_divider = _resolveComponent("v-divider")!
  const _component_v_tab = _resolveComponent("v-tab")!
  const _component_v_tabs = _resolveComponent("v-tabs")!
  const _component_v_window_item = _resolveComponent("v-window-item")!
  const _component_v_textarea = _resolveComponent("v-textarea")!
  const _component_v_window = _resolveComponent("v-window")!
  const _component_v_snackbar = _resolveComponent("v-snackbar")!
  const _component_v_container = _resolveComponent("v-container")!

  return (_openBlock(), _createBlock(_component_v_container, {
    fluid: "",
    class: "upload-container"
  }, {
    default: _withCtx(() => [
      _createVNode(_component_v_dialog, {
        modelValue: dialog.value,
        "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => ((dialog).value = $event)),
        "max-width": "400"
      }, {
        default: _withCtx(() => [
          _createVNode(_component_v_card, { class: "confirm-dialog" }, {
            default: _withCtx(() => [
              _createVNode(_component_v_card_title, { class: "text-h6" }, {
                default: _withCtx(() => [...(_cache[14] || (_cache[14] = [
                  _createTextVNode(" Open in new tab? ", -1)
                ]))]),
                _: 1
              }),
              _createVNode(_component_v_card_text, { class: "text-body-1" }, {
                default: _withCtx(() => [...(_cache[15] || (_cache[15] = [
                  _createTextVNode(" Do you want to open the file in a new tab? ", -1)
                ]))]),
                _: 1
              }),
              _createVNode(_component_v_card_actions, { class: "justify-end pa-4" }, {
                default: _withCtx(() => [
                  _createVNode(_component_v_btn, {
                    variant: "tonal",
                    onClick: _cache[0] || (_cache[0] = ($event: any) => (dialog.value = false))
                  }, {
                    default: _withCtx(() => [...(_cache[16] || (_cache[16] = [
                      _createTextVNode(" Cancel ", -1)
                    ]))]),
                    _: 1
                  }),
                  _createVNode(_component_v_btn, {
                    variant: "tonal",
                    onClick: _cache[1] || (_cache[1] = ($event: any) => (open(false)))
                  }, {
                    default: _withCtx(() => [...(_cache[17] || (_cache[17] = [
                      _createTextVNode(" This tab ", -1)
                    ]))]),
                    _: 1
                  }),
                  _createVNode(_component_v_btn, {
                    variant: "flat",
                    color: "primary",
                    onClick: _cache[2] || (_cache[2] = ($event: any) => (open(true)))
                  }, {
                    default: _withCtx(() => [...(_cache[18] || (_cache[18] = [
                      _createTextVNode(" New tab ", -1)
                    ]))]),
                    _: 1
                  })
                ]),
                _: 1
              })
            ]),
            _: 1
          })
        ]),
        _: 1
      }, 8, ["modelValue"]),
      _createVNode(_component_v_dialog, {
        modelValue: reportDialog.value,
        "onUpdate:modelValue": _cache[6] || (_cache[6] = ($event: any) => ((reportDialog).value = $event)),
        "max-width": "500"
      }, {
        default: _withCtx(() => [
          _createVNode(_component_v_card, { class: "report-dialog" }, {
            default: _withCtx(() => [
              _createVNode(_component_v_card_title, { class: "text-h6" }, {
                default: _withCtx(() => [
                  _createVNode(_component_v_icon, { start: "" }, {
                    default: _withCtx(() => [...(_cache[19] || (_cache[19] = [
                      _createTextVNode("mdi-file-document-outline", -1)
                    ]))]),
                    _: 1
                  }),
                  _cache[20] || (_cache[20] = _createTextVNode(" Report File Detected ", -1))
                ]),
                _: 1
              }),
              _createVNode(_component_v_card_text, { class: "text-body-1" }, {
                default: _withCtx(() => [
                  _cache[24] || (_cache[24] = _createElementVNode("p", null, "This file contains a saved report with an ASN.1 object.", -1)),
                  _createVNode(_component_v_divider, { class: "my-3" }),
                  _createElementVNode("div", _hoisted_1, [
                    _createElementVNode("div", null, [
                      _cache[21] || (_cache[21] = _createElementVNode("strong", null, "Name:", -1)),
                      _createTextVNode(" " + _toDisplayString(reportInfo.value.name), 1)
                    ]),
                    _createElementVNode("div", null, [
                      _cache[22] || (_cache[22] = _createElementVNode("strong", null, "Exported:", -1)),
                      _createTextVNode(" " + _toDisplayString(reportInfo.value.exportedAt), 1)
                    ]),
                    _createElementVNode("div", null, [
                      _cache[23] || (_cache[23] = _createElementVNode("strong", null, "Test Results:", -1)),
                      _createTextVNode(" " + _toDisplayString(reportInfo.value.resultCount) + " RP(s) tested ", 1)
                    ])
                  ])
                ]),
                _: 1
              }),
              _createVNode(_component_v_card_actions, { class: "justify-end pa-4" }, {
                default: _withCtx(() => [
                  _createVNode(_component_v_btn, {
                    variant: "tonal",
                    onClick: _cache[4] || (_cache[4] = ($event: any) => (reportDialog.value = false))
                  }, {
                    default: _withCtx(() => [...(_cache[25] || (_cache[25] = [
                      _createTextVNode(" Cancel ", -1)
                    ]))]),
                    _: 1
                  }),
                  _createVNode(_component_v_btn, {
                    variant: "flat",
                    color: "primary",
                    onClick: _cache[5] || (_cache[5] = ($event: any) => (loadReportObject(true)))
                  }, {
                    default: _withCtx(() => [
                      _createVNode(_component_v_icon, { start: "" }, {
                        default: _withCtx(() => [...(_cache[26] || (_cache[26] = [
                          _createTextVNode("mdi-plus", -1)
                        ]))]),
                        _: 1
                      }),
                      _cache[27] || (_cache[27] = _createTextVNode(" Load in New Tab ", -1))
                    ]),
                    _: 1
                  })
                ]),
                _: 1
              })
            ]),
            _: 1
          })
        ]),
        _: 1
      }, 8, ["modelValue"]),
      _createVNode(_component_v_card, {
        class: "upload-card",
        elevation: "0"
      }, {
        default: _withCtx(() => [
          _createVNode(_component_v_tabs, {
            modelValue: activeTab.value,
            "onUpdate:modelValue": _cache[7] || (_cache[7] = ($event: any) => ((activeTab).value = $event)),
            color: "primary",
            class: "upload-tabs"
          }, {
            default: _withCtx(() => [
              _createVNode(_component_v_tab, {
                value: "file",
                class: "tab-item"
              }, {
                default: _withCtx(() => [
                  _createVNode(_component_v_icon, { start: "" }, {
                    default: _withCtx(() => [...(_cache[28] || (_cache[28] = [
                      _createTextVNode("mdi-file-upload-outline", -1)
                    ]))]),
                    _: 1
                  }),
                  _cache[29] || (_cache[29] = _createTextVNode(" File ", -1))
                ]),
                _: 1
              }),
              _createVNode(_component_v_tab, {
                value: "paste",
                class: "tab-item"
              }, {
                default: _withCtx(() => [
                  _createVNode(_component_v_icon, { start: "" }, {
                    default: _withCtx(() => [...(_cache[30] || (_cache[30] = [
                      _createTextVNode("mdi-content-paste", -1)
                    ]))]),
                    _: 1
                  }),
                  _cache[31] || (_cache[31] = _createTextVNode(" Paste ", -1))
                ]),
                _: 1
              }),
              _createVNode(_component_v_tab, {
                value: "example",
                class: "tab-item"
              }, {
                default: _withCtx(() => [
                  _createVNode(_component_v_icon, { start: "" }, {
                    default: _withCtx(() => [...(_cache[32] || (_cache[32] = [
                      _createTextVNode("mdi-file-document-outline", -1)
                    ]))]),
                    _: 1
                  }),
                  _cache[33] || (_cache[33] = _createTextVNode(" Examples ", -1))
                ]),
                _: 1
              })
            ]),
            _: 1
          }, 8, ["modelValue"]),
          _createVNode(_component_v_divider),
          _createVNode(_component_v_window, {
            modelValue: activeTab.value,
            "onUpdate:modelValue": _cache[11] || (_cache[11] = ($event: any) => ((activeTab).value = $event)),
            class: "tab-content"
          }, {
            default: _withCtx(() => [
              _createVNode(_component_v_window_item, { value: "file" }, {
                default: _withCtx(() => [
                  _createElementVNode("div", {
                    class: _normalizeClass(["drop-zone", {
              'drop-zone--active': dragOver.value,
              'drop-zone--has-file': file.value
            }]),
                    onDragover: _cache[8] || (_cache[8] = _withModifiers(($event: any) => (dragOver.value = true), ["prevent"])),
                    onDragleave: _cache[9] || (_cache[9] = _withModifiers(($event: any) => (dragOver.value = false), ["prevent"])),
                    onDrop: _withModifiers(handleDrop, ["prevent"]),
                    onClick: triggerFileInput
                  }, [
                    _createElementVNode("input", {
                      ref_key: "fileInputRef",
                      ref: fileInputRef,
                      type: "file",
                      hidden: "",
                      multiple: "",
                      onChange: handleFileSelect
                    }, null, 544),
                    _createElementVNode("div", _hoisted_2, [
                      _createVNode(_component_v_icon, {
                        icon: 
                  file.value
                    ? 'mdi-file-check-outline'
                    : 'mdi-cloud-upload-outline'
                ,
                        color: file.value ? 'success' : 'primary',
                        size: "48",
                        class: "drop-zone__icon"
                      }, null, 8, ["icon", "color"]),
                      (!file.value)
                        ? (_openBlock(), _createElementBlock("div", _hoisted_3, [...(_cache[34] || (_cache[34] = [
                            _createElementVNode("span", { class: "drop-zone__title" }, " Drop files here ", -1),
                            _createElementVNode("span", { class: "drop-zone__subtitle" }, " or click to browse ", -1)
                          ]))]))
                        : (_openBlock(), _createElementBlock("div", _hoisted_4, [
                            _createElementVNode("span", _hoisted_5, _toDisplayString(file.value.name), 1),
                            _createVNode(_component_v_btn, {
                              icon: "mdi-close",
                              size: "x-small",
                              variant: "text",
                              class: "drop-zone__clear",
                              onClick: _withModifiers(clearFile, ["stop"])
                            })
                          ]))
                    ]),
                    _cache[35] || (_cache[35] = _createElementVNode("div", { class: "drop-zone__hint" }, " Supports DER, PEM, Base64, JSON, and Report files ", -1))
                  ], 34)
                ]),
                _: 1
              }),
              _createVNode(_component_v_window_item, { value: "paste" }, {
                default: _withCtx(() => [
                  _createElementVNode("div", _hoisted_6, [
                    _createVNode(_component_v_textarea, {
                      modelValue: pastedContent.value,
                      "onUpdate:modelValue": _cache[10] || (_cache[10] = ($event: any) => ((pastedContent).value = $event)),
                      variant: "outlined",
                      label: "Paste Base64, Hex, or PEM content",
                      placeholder: "Paste your encoded content here...",
                      rows: "6",
                      "auto-grow": "",
                      "hide-details": "",
                      class: "paste-textarea"
                    }, null, 8, ["modelValue"]),
                    _createVNode(_component_v_btn, {
                      color: "primary",
                      variant: "flat",
                      block: "",
                      class: "mt-4",
                      disabled: !pastedContent.value.trim(),
                      onClick: handlePastedContent
                    }, {
                      default: _withCtx(() => [
                        _createVNode(_component_v_icon, { start: "" }, {
                          default: _withCtx(() => [...(_cache[36] || (_cache[36] = [
                            _createTextVNode("mdi-check", -1)
                          ]))]),
                          _: 1
                        }),
                        _cache[37] || (_cache[37] = _createTextVNode(" Load Content ", -1))
                      ]),
                      _: 1
                    }, 8, ["disabled"])
                  ])
                ]),
                _: 1
              }),
              _createVNode(_component_v_window_item, { value: "example" }, {
                default: _withCtx(() => [
                  _createElementVNode("div", _hoisted_7, [
                    (_openBlock(), _createElementBlock(_Fragment, null, _renderList(examples, (example) => {
                      return _createVNode(_component_v_btn, {
                        key: example.type,
                        variant: "tonal",
                        color: "primary",
                        class: "example-btn",
                        onClick: ($event: any) => (loadExample(example.type))
                      }, {
                        default: _withCtx(() => [
                          _createVNode(_component_v_icon, { start: "" }, {
                            default: _withCtx(() => [
                              _createTextVNode(_toDisplayString(example.icon), 1)
                            ]),
                            _: 2
                          }, 1024),
                          _createTextVNode(" " + _toDisplayString(example.label), 1)
                        ]),
                        _: 2
                      }, 1032, ["onClick"])
                    }), 64))
                  ]),
                  _cache[38] || (_cache[38] = _createElementVNode("p", { class: "examples-hint" }, " Load a sample RPKI object to explore the editor ", -1))
                ]),
                _: 1
              })
            ]),
            _: 1
          }, 8, ["modelValue"])
        ]),
        _: 1
      }),
      _createVNode(_component_v_snackbar, {
        modelValue: errorSnackbar.value,
        "onUpdate:modelValue": _cache[13] || (_cache[13] = ($event: any) => ((errorSnackbar).value = $event)),
        color: "error",
        location: "bottom",
        timeout: 4000
      }, {
        actions: _withCtx(() => [
          _createVNode(_component_v_btn, {
            variant: "text",
            onClick: _cache[12] || (_cache[12] = ($event: any) => (errorSnackbar.value = false))
          }, {
            default: _withCtx(() => [...(_cache[39] || (_cache[39] = [
              _createTextVNode(" Close ", -1)
            ]))]),
            _: 1
          })
        ]),
        default: _withCtx(() => [
          _createTextVNode(_toDisplayString(errorMessage.value) + " ", 1)
        ]),
        _: 1
      }, 8, ["modelValue"])
    ]),
    _: 1
  }))
}
}

})